import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { formEndpoints } from '../../services/api/endpoints/form.endpoint';
import { CreateFormDTO, UpdateFormDTO, Form } from '../../types/form';
import { logger } from '../../utils/logger';

export const useFormActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createForm = useCallback(
    async (data: CreateFormDTO): Promise<Form> => {
      if (!selectedOrganizationId) {
        throw new Error('No organization selected');
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await formEndpoints.createForm(selectedOrganizationId, data);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create form';
        setError(message);
        logger.error('Error creating form:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrganizationId]
  );

  // uuid is the API identifier, formId is the form's internal ID
  const updateForm = useCallback(
    async (uuid: string, data: UpdateFormDTO, formId?: string): Promise<Form> => {
      if (!selectedOrganizationId) {
        throw new Error('No organization selected');
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await formEndpoints.updateForm(selectedOrganizationId, uuid, data, formId);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update form';
        setError(message);
        logger.error('Error updating form:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrganizationId]
  );

  // uuid is the API identifier
  const deleteForm = useCallback(
    async (uuid: string): Promise<void> => {
      if (!selectedOrganizationId) {
        throw new Error('No organization selected');
      }

      setIsLoading(true);
      setError(null);

      try {
        await formEndpoints.deleteForm(selectedOrganizationId, uuid);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete form';
        setError(message);
        logger.error('Error deleting form:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrganizationId]
  );

  // uuid is the API identifier
  const getForm = useCallback(
    async (uuid: string): Promise<Form> => {
      if (!selectedOrganizationId) {
        throw new Error('No organization selected');
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await formEndpoints.getForm(selectedOrganizationId, uuid);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch form';
        setError(message);
        logger.error('Error fetching form:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrganizationId]
  );

  return {
    createForm,
    updateForm,
    deleteForm,
    getForm,
    isLoading,
    error,
  };
};
